import { useState, useEffect } from 'react';
import { FiUser, FiSave, FiCamera } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { updateProfile } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { PK_CITIES } from '../../data/regions';
import './ProfilePage.css';

export default function ProfilePage() {
  const { user, updateUser } = useAuth();

  const [form, setForm] = useState({
    name: '', phone: '', street: '', city: '', state: '', zipCode: '',
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        phone: user.phone || '',
        street: user.address?.street || '',
        city: user.address?.city || '',
        state: user.address?.state || '',
        zipCode: user.address?.zipCode || '',
      });
      setAvatarPreview(user.avatar || '');
    }
  }, [user]);

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (errors[field]) setErrors((er) => ({ ...er, [field]: '' }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Image must be under 5 MB');
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (form.phone && !/^\d{10}$/.test(form.phone.replace(/\D/g, ''))) {
      errs.phone = 'Enter a valid 10-digit number (e.g. 3001234567)';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('phone', form.phone ? `+92 ${form.phone}` : '');
      fd.append('address', JSON.stringify({
        street: form.street,
        city: form.city,
        state: form.state,
        zipCode: form.zipCode,
        country: 'Pakistan',
      }));
      if (avatarFile) fd.append('avatar', avatarFile);
      const { data } = await updateProfile(fd);
      updateUser(data.user);
      toast.success('Profile updated successfully!');
      setAvatarFile(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  return (
    <div className="profile-page">
      <div className="container">
        <div className="page-header">
          <h1><FiUser size={22} /> My Profile</h1>
          <p className="text-muted">Manage your personal information</p>
        </div>

        <div className="profile-layout">
          {/* Avatar panel */}
          <aside className="profile-avatar-panel card">
            <div className="card-body avatar-panel-body">
              <div className="avatar-wrap">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar" className="profile-avatar-img" />
                ) : (
                  <div className="profile-avatar-placeholder">{initials}</div>
                )}
                <label className="avatar-upload-btn" htmlFor="avatarInput" title="Change photo">
                  <FiCamera size={14} />
                  <input id="avatarInput" type="file" accept="image/jpeg,image/png,image/webp"
                    onChange={handleAvatarChange} style={{ display: 'none' }} />
                </label>
              </div>
              <h2 className="avatar-name">{user?.name}</h2>
              <p className="avatar-email">{user?.email}</p>
              <span className={`badge ${user?.role === 'admin' ? 'badge-danger' : user?.role === 'vendor' ? 'badge-primary' : 'badge-success'}`}>
                {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
              </span>
              <p className="avatar-hint">Click the camera icon to change your photo</p>
            </div>
          </aside>

          {/* Form */}
          <form className="profile-form card" onSubmit={handleSubmit}>
            <div className="card-header">
              <h2 className="profile-form-title">Personal Information</h2>
            </div>
            <div className="card-body">
              <div className="profile-section-label">Basic Details</div>

              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className={`form-input${errors.name ? ' error' : ''}`} value={form.name}
                  onChange={set('name')} placeholder="Muhammad Ali" />
                {errors.name && <span className="form-error">{errors.name}</span>}
              </div>

              {/* Phone with static +92 prefix */}
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <div className="phone-input-wrap">
                  <span className="phone-code-static">+92</span>
                  <input
                    className={`form-input phone-number-input${errors.phone ? ' error' : ''}`}
                    value={form.phone} onChange={set('phone')}
                    placeholder="3001234567" type="tel"
                  />
                </div>
                {errors.phone && <span className="form-error">{errors.phone}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input className="form-input" value={user?.email || ''} disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                <span className="form-hint">Email cannot be changed here.</span>
              </div>

              <div className="profile-section-label" style={{ marginTop: '1.25rem' }}>Shipping Address</div>

              <div className="form-group">
                <label className="form-label">Street Address</label>
                <input className="form-input" value={form.street} onChange={set('street')}
                  placeholder="House 12, Street 4, Block B" />
              </div>

              <div className="form-row-2">
                {/* City dropdown */}
                <div className="form-group">
                  <label className="form-label">City</label>
                  <select className="form-select" value={form.city} onChange={set('city')}>
                    <option value="">Select City</option>
                    {PK_CITIES.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Province</label>
                  <input className="form-input" value={form.state} onChange={set('state')} placeholder="Punjab" />
                </div>
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Postal Code</label>
                  <input className="form-input" value={form.zipCode} onChange={set('zipCode')} placeholder="75500" />
                </div>
                <div className="form-group">
                  <label className="form-label">Country</label>
                  <input className="form-input" value="Pakistan" disabled style={{ opacity: 0.6, cursor: 'not-allowed' }} />
                </div>
              </div>

              <div className="profile-form-actions">
                <button type="submit" className="btn btn-primary btn-lg" disabled={saving}>
                  {saving ? 'Saving…' : <><FiSave size={16} /> Save Changes</>}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
